import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from '@/features/tenants/useTenants';
import { formatCents, useUpdatePlan, type PlanTier, type TierSummary } from './usePlans';

/**
 * Edit a tenant's package — canvas `5f` (slice 20).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE FIRST SURFACE IN THE PRODUCT THAT CAN PRICE A CUSTOMER.
 * ─────────────────────────────────────────────────────────────────────────────
 * Until this shipped, `tenants.monthly_rate_cents` could only be set at tenant
 * creation (slice 18b) or by hand in SQL. The pilot's $6 was entered by hand;
 * Raad Internal's was never entered at all, which is why it sits on the unbilled
 * list. A column with no edit surface is a column that drifts.
 *
 * THE BAND IS SHOWN AND NOT ENFORCED. §7 prices Fleet at $8–12 and the card says
 * so, but the input accepts anything: the schema's own comment records that a
 * per-tenant rate exists precisely so a discounted or free customer can be
 * expressed, and a validator rejecting $0 would delete that capability to police
 * a price list nobody promised was exhaustive.
 *
 * BLANK AND ZERO ARE DIFFERENT AND BOTH REACHABLE. Blank sends `null` — "no rate
 * recorded". A typed `0` sends `0` — "deliberately not charged". The dialog says
 * which is which, because the two look identical on a screen and mean opposite
 * things on the unbilled list.
 */

const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary';

/**
 * Dollars as typed → integer cents, or `null` when blank.
 *
 * Exported for its own test. `''` → `null` and `'0'` → `0` is the whole point;
 * money is integer cents and this is the only conversion on the screen.
 */
export function rateToCents(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100);
}

/** Cents → the dollars string an input should start with. `null` → blank. */
export function centsToInput(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2);
}

export function EditPlanDialog({
  tenant,
  tiers,
  onClose,
}: {
  tenant: AdminTenant;
  tiers: TierSummary[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState<PlanTier>(tenant.plan);
  const [rate, setRate] = useState(centsToInput(tenant.monthlyRateCents));
  /**
   * 🔴 HAS A HUMAN TOUCHED THE RATE FIELD? Until they do, the rate FOLLOWS the
   * tier (Mahad, 4 Sep: *"if some one adds vehicle plan price should take
   * automatically, the only place price should be adjusted is edit plan"*).
   *
   * Tracking the touch is what keeps the two cases apart. Without it, switching
   * Track → Fleet would either strand the old $6 in the box (and save it as a
   * deliberate discount nobody chose) or silently overwrite a genuine override
   * an operator had just typed. Neither is recoverable from the audit log
   * afterwards, because both look like a number someone entered.
   */
  const [rateTouched, setRateTouched] = useState(false);
  const [error, setError] = useState(false);
  const update = useUpdatePlan(tenant.id);

  const guide = tiers.find((x) => x.tier === plan);

  async function submit() {
    setError(false);
    try {
      await update.mutateAsync({ plan, monthlyRateCents: rateToCents(rate) });
      onClose();
    } catch {
      setError(true);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('plans.edit.title', { name: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-md sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('plans.edit.title', { name: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('plans.close')}
          </button>
        </div>

        <div className="grid gap-4">
          <div>
            <label className="block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('plans.edit.plan')}
              </span>
              <select
                className={INPUT}
                value={plan}
                onChange={(e) => {
                  const next = e.target.value as PlanTier;
                  setPlan(next);
                  // The rate follows the tier until someone edits it themselves.
                  if (!rateTouched) {
                    const card = tiers.find((x) => x.tier === next);
                    if (card) setRate(centsToInput(card.cents));
                  }
                }}
              >
                <option value="track">{t('plans.tier.track')}</option>
                <option value="fleet">{t('plans.tier.fleet')}</option>
                <option value="pro">{t('plans.tier.pro')}</option>
              </select>
            </label>
            {guide !== undefined ? (
              <span className="mt-1 block text-[11.5px] text-raad-muted">
                {t('plans.edit.guide', { price: formatCents(guide.cents) })}
              </span>
            ) : null}
          </div>

          <div>
            <label className="block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('plans.edit.rate')}
              </span>
              <input
                className={INPUT}
                inputMode="decimal"
                value={rate}
                onChange={(e) => {
                  setRateTouched(true);
                  setRate(e.target.value);
                }}
              />
            </label>
            {/* Said out loud, because the two states look the same in an input
                and mean opposite things on the unbilled list. */}
            <span className="mt-1 block text-[11.5px] text-raad-muted">
              {t('plans.edit.rateHint')}
            </span>
          </div>
        </div>

        {error ? (
          <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
            {t('plans.edit.failed')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={update.isPending}
            className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-50"
          >
            {update.isPending ? t('plans.edit.saving') : t('plans.edit.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
